import { useState, useCallback } from 'react';
import { Loader2, TrendingUp, TrendingDown, History, Plus } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, FipeBusca, type FipeResultado } from '../ui';
import { useApiEffect } from '../../hooks';
import type { HistoricoFipe, ConsultaFipe, FipeResumo } from '../../types';
import { formatCurrency, formatDate } from '../../utils/format';

interface VeiculoInfo {
  marcaNome: string;
  modeloNome: string;
  anoModelo: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

export function FipeVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showApiError } = useNotification();

  // Dados do veículo para pré-popular busca
  const [veiculoInfo, setVeiculoInfo] = useState<VeiculoInfo | null>(null);
  const [codigoFipeAtual, setCodigoFipeAtual] = useState<string>('');

  // Histórico e resumo
  const [historico, setHistorico] = useState<HistoricoFipe[]>([]);
  const [resumo, setResumo] = useState<FipeResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Busca FIPE
  const [showBusca, setShowBusca] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Carregar dados do veículo e histórico
  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const [veiculoRes, historicoRes, resumoRes] = await Promise.all([
        api.get<VeiculoInfo>(`/veiculos/${veiculoId}`, { signal }),
        api.get<HistoricoFipe[]>(`/veiculos/${veiculoId}/fipe/historico`, { signal }),
        api.get<FipeResumo>(`/veiculos/${veiculoId}/fipe/resumo`, { signal }),
      ]);
      setVeiculoInfo({
        marcaNome: veiculoRes.data.marcaNome,
        modeloNome: veiculoRes.data.modeloNome,
        anoModelo: veiculoRes.data.anoModelo,
      });
      setHistorico(historicoRes.data);
      setResumo(resumoRes.data);

      // Pegar código FIPE do último registro se existir
      if (historicoRes.data.length > 0 && historicoRes.data[0].codigoFipe) {
        setCodigoFipeAtual(historicoRes.data[0].codigoFipe);
      }
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleResultado = async (resultado: FipeResultado) => {
    // Extrair valor numérico
    const valorStr = resultado.valor.replace('R$', '').replace(/\./g, '').replace(',', '.').trim();
    const valorNum = parseFloat(valorStr);
    if (isNaN(valorNum)) {
      return;
    }

    // Extrair mês de referência (ex: "maio de 2024" -> "2024-05")
    const mesRef = resultado.mesReferencia.toLowerCase();
    const meses: Record<string, string> = {
      janeiro: '01', fevereiro: '02', março: '03', marco: '03', abril: '04',
      maio: '05', junho: '06', julho: '07', agosto: '08',
      setembro: '09', outubro: '10', novembro: '11', dezembro: '12',
    };
    let mesReferencia = new Date().toISOString().slice(0, 7);
    for (const [nomeMes, numMes] of Object.entries(meses)) {
      if (mesRef.includes(nomeMes)) {
        const anoMatch = mesRef.match(/\d{4}/);
        if (anoMatch) {
          mesReferencia = `${anoMatch[0]}-${numMes}`;
        }
        break;
      }
    }

    const consulta: ConsultaFipe = {
      codigoFipe: resultado.codigoFipe,
      mesReferencia,
      valorFipe: valorNum,
    };

    setIsSubmitting(true);
    try {
      await api.post(`/veiculos/${veiculoId}/fipe/consulta`, consulta);
      showSuccess('Consulta FIPE registrada com sucesso');
      setShowBusca(false);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatMesReferencia = (mes?: string) => {
    if (!mes) return '-';
    const [ano, mesNum] = mes.split('-');
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${meses[parseInt(mesNum) - 1]}/${ano}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Histórico FIPE - ${veiculoNome}`} size="lg">
      <div className="space-y-4">
        {/* Resumo */}
        {resumo && resumo.valorAtual && (
          <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Último Valor FIPE</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(resumo.valorAtual)}</p>
                {resumo.mesReferencia && (
                  <p className="text-xs text-gray-500">Ref: {formatMesReferencia(resumo.mesReferencia)}</p>
                )}
              </div>
              {resumo.variacaoPercentual !== undefined && resumo.variacaoPercentual !== null && (
                <div>
                  <p className="text-sm text-gray-500">Variação</p>
                  <div className="flex items-center gap-2">
                    {resumo.variacaoPercentual >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                    <span
                      className={`text-xl font-bold ${
                        resumo.variacaoPercentual >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {resumo.variacaoPercentual >= 0 ? '+' : ''}
                      {resumo.variacaoPercentual.toFixed(1)}%
                    </span>
                  </div>
                  {resumo.variacao !== undefined && resumo.variacao !== null && (
                    <p className="text-xs text-gray-500">
                      {resumo.variacao >= 0 ? '+' : ''}{formatCurrency(resumo.variacao)}
                    </p>
                  )}
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Total de Consultas</p>
                <p className="text-xl font-bold text-gray-700">{resumo.totalConsultas}</p>
                {resumo.ultimaConsulta && (
                  <p className="text-xs text-gray-500">Última: {formatDate(resumo.ultimaConsulta)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Botão Nova Consulta ou Busca FIPE */}
        {!showBusca ? (
          <div className="flex justify-end">
            <button
              onClick={() => setShowBusca(true)}
              disabled={!veiculoInfo || isSubmitting}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Nova Consulta FIPE
            </button>
          </div>
        ) : veiculoInfo && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <FipeBusca
              marcaNome={veiculoInfo.marcaNome}
              modeloNome={veiculoInfo.modeloNome}
              anoModelo={veiculoInfo.anoModelo}
              codigoFipeInicial={codigoFipeAtual}
              onResultado={handleResultado}
              onCancel={() => setShowBusca(false)}
              modoInicial={codigoFipeAtual ? 'codigo' : 'filtros'}
            />
          </div>
        )}

        {/* Histórico */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : historico.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Nenhuma consulta FIPE registrada</p>
              <p className="text-sm text-gray-400">Clique em "Nova Consulta FIPE" para começar</p>
            </div>
          ) : (
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Mês Ref.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Valor</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Variação</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Consultado em</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {historico.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {formatMesReferencia(item.mesReferencia)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                        {formatCurrency(item.valorFipe)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.variacaoPercentual !== undefined && item.variacaoPercentual !== null ? (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              item.variacaoPercentual >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {item.variacaoPercentual >= 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : (
                              <TrendingDown className="w-3 h-3" />
                            )}
                            {item.variacaoPercentual >= 0 ? '+' : ''}
                            {item.variacaoPercentual.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500">
                        {formatDate(item.dataConsulta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
